import { type CSSProperties, useState } from 'react'
import { Box } from '@mui/material'
import classNames from 'classnames'
import { Error } from 'shared/ui/Error'
import { type ILabelProps, Label } from 'shared/ui/Label'
import { HelperText, type IHelperTextProps } from 'shared/ui/HelperText'
import { makeTextFieldHighlightLimit, TextField } from 'shared/ui/TextField'
import { Warning } from 'shared/ui/Warning'
import type { ITextFieldBaseAsideAction, ITextFieldProps } from '../TextField/types'

import styles from './styles.module.scss'

export type ITextFieldWithLabelProps = {
  marginTop?: CSSProperties['marginTop']
  marginRight?: CSSProperties['marginRight']
  marginLeft?: CSSProperties['marginLeft']
  marginBottom?: CSSProperties['marginBottom']
  labelProps?: ILabelProps
  textFieldProps: ITextFieldProps
  bottomLabelProps?: ILabelProps
  errorMessage?: string
  errorClassName?: string
  errorBorder?: boolean
  helperTextProps?: IHelperTextProps
  leftActions?: ITextFieldBaseAsideAction[]
  rightActions?: ITextFieldBaseAsideAction[]
  limit?: number
  widthWrap?: CSSProperties['width']
}

export const TextFieldWithLabel = ({
  marginTop = 0,
  marginRight = 0,
  marginBottom = 0,
  marginLeft = 0,
  labelProps,
  textFieldProps,
  bottomLabelProps,
  errorMessage,
  errorClassName,
  errorBorder,
  helperTextProps,
  leftActions,
  rightActions,
  limit,
  widthWrap = '100%',
}: ITextFieldWithLabelProps) => {
  const [limitReached, setLimitReached] = useState(false)

  return (
    <Box
      width={widthWrap}
      marginTop={marginTop}
      marginRight={marginRight}
      marginLeft={marginLeft}
      marginBottom={marginBottom}
    >
      {labelProps?.label && <Label marginBottom={4} {...labelProps} />}
      <TextField
        variant={'stroke'}
        size={'medium'}
        {...textFieldProps}
        error={errorMessage || errorBorder}
        leftActions={leftActions}
        rightActions={rightActions}
        mainActions={[
          makeTextFieldHighlightLimit({
            limit: limit ?? Number.POSITIVE_INFINITY,
            onChange: setLimitReached,
          }),
          ...(textFieldProps.mainActions ?? []),
        ]}
      />
      {bottomLabelProps && <Label {...bottomLabelProps} />}
      <Error
        type='text'
        error={errorMessage}
        size='small'
        wrapClassName={classNames(styles.error, errorClassName)}
      />
      {limit !== null && limit !== undefined && limitReached && (
        <Warning text={`Cannot be longer than ${limit} characters`} className={styles.warning} />
      )}
      {helperTextProps && (
        <div className={styles.warning}>
          <HelperText {...helperTextProps} />
        </div>
      )}
    </Box>
  )
}
